import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from './store/userSlice';
import useIdleTimer from './hooks/useIdleTimer';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Spinner from './components/Spinner';
import GravityLogo from './components/GravityLogo';
import SessionExpiryModal from './components/SessionExpiryModal';
import Dashboard from './components/Dashboard';
import Products from './components/Products';
import ProductTypes from './components/ProductTypes';
import CalendarSettings from './components/CalendarSettings';
import Coupon from './components/Coupon';
import Users from './components/Users';
import Stores from './components/Stores';
import UserStoreRights from './components/UserStoreRights';
import Transactions from './components/Transactions';
import BonusPrizes from './components/BonusPrizes';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, roleId, roleName } = useSelector(s => s.user);
  const [page, setPage] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const loadTimer = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('lottery.sidebarCollapsed') === 'true'
  );
  const [sessionWarning, setSessionWarning] = useState(null);

  /* ── Each page now loads its own data on mount — no bulk load here ── */

  const handleNavigate = useCallback((p) => {
    if (p === page) return;
    setLoading(true);
    if (loadTimer.current) clearTimeout(loadTimer.current);
    loadTimer.current = setTimeout(() => { setPage(p); setLoading(false); }, 150);
    if (window.innerWidth <= 640) setSidebarCollapsed(true);
  }, [page]);

  useEffect(() => () => { if (loadTimer.current) clearTimeout(loadTimer.current); }, []);

  const handleWarning = useCallback((sec) => setSessionWarning(sec), []);
  const handleExpire = useCallback(() => {
    setSessionWarning(null);
    dispatch(logout());
  }, [dispatch]);

  const { resetTimer } = useIdleTimer(handleWarning, handleExpire, isAuthenticated);

  const handleStay = () => { resetTimer(); setSessionWarning(null); };
  const handleLogout = () => { setSessionWarning(null); dispatch(logout()); };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('lottery.sidebarCollapsed', String(!prev));
      return !prev;
    });
  };

  if (!isAuthenticated) return <Login />;

  const initials = user?.userName?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <Dashboard />;
      case 'products': return <Products />;
      case 'product-types': return <ProductTypes />;
      case 'calendar': return <CalendarSettings />;
      case 'bonus-prizes': return <BonusPrizes />;
      case 'coupon': return <Coupon />;
      case 'transactions': return <Transactions />;
      case 'users': return <Users />;
      case 'stores': return <Stores />;
      case 'rights': return <UserStoreRights />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="app-layout">
      {!sidebarCollapsed && <div className="sidebar-overlay" onClick={() => setSidebarCollapsed(true)} />}
      <Sidebar
        activePage={page}
        onNavigate={handleNavigate}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        roleId={roleId}
      />
      <div className="main">
        <div className="topbar">
          <button className="topbar-hamburger" onClick={toggleSidebar}>☰</button>
          <div className="topbar-brand">
            <span className="topbar-title">Gravity - Instance Rewards</span>
          </div>
          <div className="topbar-right">
            <div className="topbar-user">
              <div className="topbar-avatar">{initials}</div>
              <span className="topbar-username">{user?.userName}</span>
            </div>
            <span className="topbar-role">{roleName}</span>
            <button className="topbar-logout" onClick={handleLogout}>Logout</button>
          </div>
        </div>
        <div className="page-content">
          {loading ? (
            <div className="page-loading"><Spinner /></div>
          ) : renderPage()}
          <div className="page-footer">
            <GravityLogo size={18} circle />
            <span>Gravity Soft Net</span>
          </div>
        </div>
      </div>
      {sessionWarning && (
        <SessionExpiryModal secondsLeft={sessionWarning} onStay={handleStay} onLogout={handleLogout} />
      )}
    </div>
  );
}

export default App;
